import { inject, Injectable } from '@angular/core';
import { Observable, take } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ApiHttpService } from '../../api-http.service';
import { Client, ClientEmployee, CustomHttpResponse, HydraCollectionResponse } from '../../../models';
import { withQueryString } from '../../../utils/url-utils/url-utils';

@Injectable({
  providedIn: 'root',
})
export class ClientEmployeeService {
  private apiService: ApiHttpService<ClientEmployee> = inject(ApiHttpService<ClientEmployee>);
  divisions = this.apiService.getAll('/client_employees').pipe(
    take(1),
    filter((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => !!result.body),
    map((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => result.body['hydra:member']),
  );

  getEmployees() {
    return this.apiService.getMany(`/client_employees`).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => !!result.body),
      map((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => result.body['hydra:member']),
    );
  }

  getClientEmployees(client: Client, search?: string) {
    const url = search ? `/clients/${client.id}/employees?${search}` : `/clients/${client.id}/employees`;
    return this.apiService.getMany(url).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => !!result.body),
      map((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => result.body['hydra:member']),
    );
  }

  getActiveClientEmployees(client: Client, search?: string): Observable<ClientEmployee[]> {
    const url = search
      ? `/clients/${client.id}/employees?isActive=true&${search}`
      : `/clients/${client.id}/employees?isActive=true`;
    return this.apiService.getMany(url).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => !!result.body),
      map((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => result.body['hydra:member']),
    );
  }

  getEmailListClientEmployees(
    emailListIri: string,
    search?: string,
  ): Observable<HydraCollectionResponse<ClientEmployee>> {
    const url = search ? `${emailListIri}/client_employees?${search}` : `${emailListIri}/client_employees`;
    return this.apiService.getMany(url).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => !!result.body),
      map((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => result.body),
    );
  }

  getActiveClientEmployeesIri(clientIri: string, search?: string): Observable<ClientEmployee[]> {
    const url = search ? `${clientIri}/employees?isActive=true&${search}` : `${clientIri}/employees?isActive=true`;
    return this.apiService.getMany(url).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => !!result.body),
      map((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => result.body['hydra:member']),
    );
  }

  getClientEmployeeByIri(iri: string): Observable<ClientEmployee> {
    return this.apiService.get(iri).pipe(map((result: CustomHttpResponse<ClientEmployee>) => result.body));
  }

  getClientEmployeeById(id: string): Observable<ClientEmployee> {
    return this.apiService.get(`/client_employees/${id}`).pipe(
      take(1),
      filter((result: CustomHttpResponse<ClientEmployee>) => !!result.body),
      map((result: CustomHttpResponse<ClientEmployee>) => result.body),
    );
  }

  findClientEmployee(filterTerm: string): Observable<ClientEmployee[]> {
    return this.apiService.getMany(`/client_employees?${filterTerm}`).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => !!result.body),
      map((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => result.body['hydra:member']),
    );
  }

  deleteClientEmployee(clientEmployee: ClientEmployee) {
    return this.apiService.delete(`/client_employees/${clientEmployee.id}`).pipe(map((result) => result));
  }

  /** Server-paged client employees for an arbitrary API-Platform query string. */
  getClientEmployeesPaged(search?: string): Observable<HydraCollectionResponse<ClientEmployee>> {
    const url = search ? `/client_employees?${search}` : `/client_employees`;
    return this.apiService.getMany(url).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => !!result.body),
      map((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => result.body),
    );
  }

  findActive(searchTerm?: string): Observable<HydraCollectionResponse<ClientEmployee>> {
    return this.apiService.getMany(withQueryString('/client_employees?active=true', searchTerm)).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<ClientEmployee>>) => !!result.body),
      map((result) => result.body),
    );
  }

  updateClientEmployee(iri: string, clientEmployee: ClientEmployee): Observable<ClientEmployee> {
    // An emptied address is sent as null, but a partial update (e.g. only the access rights) must not clear it.
    const body =
      'defaultDeliveryAddress' in clientEmployee && !clientEmployee.defaultDeliveryAddress
        ? { ...clientEmployee, defaultDeliveryAddress: null }
        : clientEmployee;
    return this.apiService
      .patch(iri, body)
      .pipe(map((result: CustomHttpResponse<ClientEmployee>) => result.body));
  }

  createClientEmployee(clientEmployee: ClientEmployee): Observable<ClientEmployee> {
    if (!clientEmployee.defaultDeliveryAddress) {
      clientEmployee.defaultDeliveryAddress = null;
    }
    return this.apiService
      .post('/client_employees', clientEmployee)
      .pipe(map((result: CustomHttpResponse<ClientEmployee>) => result.body));
  }

  resetPassword(clientEmployee: ClientEmployee): Observable<ClientEmployee> {
    return this.apiService
      .post(`${clientEmployee.user}/password_change`, clientEmployee)
      .pipe(map((result: CustomHttpResponse<ClientEmployee>) => result.body));
  }
}
